/**
 * Row-level operations for the review grid — duplicate / delete / create whole
 * line items, ad groups and asset groups.
 *
 * Everything else in the edit layer changes VALUES inside an existing row. This
 * module changes which rows exist, which brings three hazards the value path
 * never had. All three are handled here rather than left to callers:
 *
 *  1. ROW IDS SHIFT. GroundTruth rows are `gt-ag-${idx}` and Google Ads rows are
 *     `gads-${ci}-${ai}` — purely positional — and DV360/TTD fall back to
 *     `li-${idx}` / `ag-${idx}` when a template carries no entity ids (common
 *     pre-launch). Deleting row 2 silently re-points every later row id. So each
 *     applier RESOLVES every op to an entity reference first, then mutates:
 *     creates append (which cannot shift anything), and deletes run last in
 *     DESCENDING index order.
 *  2. DANGLING PARENT REFERENCES. A DV360 insertion order holds
 *     `io.lineItems: [lineItemId]` and a TTD campaign holds
 *     `Campaigns[].AdGroups: [AdGroupId]`. Removing an entity without removing
 *     its ref leaves the launcher in undefined behaviour — and
 *     checkLaunchTemplateSanity refuses the save outright
 *     (DANGLING_LINE_ITEM / DANGLING_AD_GROUP). Deletes prune the refs; clones
 *     add one to the same parent the template belonged to.
 *  3. NEW ENTITIES MUST BE VALID. Each platform's launcher validates against a
 *     strict per-level key allowlist (dv360_campaign_validator.py:424 rejects an
 *     unknown key outright), so there is no build-from-scratch path: a new row is
 *     always a deep clone of an existing sibling with a fresh reference id.
 *
 * On reference ids: the template's `lineItemId` / `AdGroupId` is a
 * TEMPLATE-LOCAL correlation key, not a platform id — the real id comes back in
 * the create response and the launcher maps the two
 * (dv360_campaign_launcher.py:294 keys off `original_line_item_id`). So minting
 * one for a clone is safe; we keep the shape numeric where the existing ids are
 * numeric so nothing downstream that coerces them breaks.
 *
 * Appliers return { created, deleted } where `created` is
 * [{ rowId, overrides }] — the caller then runs its NORMAL field applier over
 * those overrides, so a cloned row's values are written by exactly the same
 * writers a typed edit uses. That is deliberate: this module knows about row
 * identity and nothing about fields.
 */

const asArray = (v) => (Array.isArray(v) ? v : []);

/**
 * Mint `count` fresh reference ids that collide with nothing in `existing`.
 * Numeric when every existing id is numeric (so a downstream int() coercion
 * still works), suffixed otherwise.
 */
export function mintRefIds(existing, templateId, count) {
  const taken = new Set([...existing].map(String));
  const numericIds = [...taken].filter((id) => /^\d+$/.test(id));
  const allNumeric = numericIds.length > 0 && numericIds.length === taken.size;
  const out = [];
  if (allNumeric) {
    let next = numericIds.reduce((m, id) => Math.max(m, Number(id)), 0) + 1;
    while (out.length < count) {
      const candidate = String(next++);
      if (!taken.has(candidate)) { taken.add(candidate); out.push(candidate); }
    }
    return out;
  }
  const base = templateId ? String(templateId) : 'row';
  let n = 1;
  while (out.length < count) {
    const candidate = `${base}__copy${n++}`;
    if (!taken.has(candidate)) { taken.add(candidate); out.push(candidate); }
  }
  return out;
}

/** Split ops into the three kinds, preserving order within each. */
function partition(ops) {
  const creates = [];
  const clones = [];
  const deletes = [];
  for (const op of asArray(ops)) {
    if (!op?.op) continue;
    if (op.op === 'create') creates.push(op);
    else if (op.op === 'duplicate') clones.push(op);
    else if (op.op === 'delete') deletes.push(op);
  }
  return { creates, clones, deletes };
}

const clone = (obj) => JSON.parse(JSON.stringify(obj));

// ---------------------------------------------------------------------------
// DV360 — lineItems[] entities, insertionOrders[].lineItems[] string refs.
// ---------------------------------------------------------------------------

const dv360Id = (li) => li?.lineItemId ?? li?.LineItemId ?? null;

function dv360IndexOf(lis, rowId) {
  const byId = lis.findIndex((li) => String(dv360Id(li)) === String(rowId));
  if (byId >= 0) return byId;
  // A `li-N` row id is only produced when the entity had no id at all, so the
  // positional read is the builder's own addressing, not a guess.
  const m = /^li-(\d+)$/.exec(String(rowId));
  return m ? Number(m[1]) : -1;
}

export function applyDv360RowOps(edited, ops) {
  const created = [];
  const deleted = [];
  const { creates, clones, deletes } = partition(ops);
  if (!creates.length && !clones.length && !deletes.length) return { created, deleted };

  if (!Array.isArray(edited.lineItems)) edited.lineItems = [];
  const lis = edited.lineItems;
  const ios = asArray(edited.insertionOrders);

  const ownerOf = (id) => ios.find((io) => asArray(io.lineItems).some((r) => String(r) === String(id)));
  const existingIds = () => new Set(lis.map(dv360Id).filter(Boolean).map(String));

  // ── creates + duplicates (append only; no index shifts) ──
  const cloneJobs = [
    ...clones.map((op) => ({ templateRowId: op.rowId, count: 1, overrides: {} })),
    ...creates.map((op) => ({
      templateRowId: op.templateRowId,
      count: Math.max(1, Number(op.count) || 1),
      overrides: op.overrides || {},
    })),
  ];
  for (const job of cloneJobs) {
    const idx = dv360IndexOf(lis, job.templateRowId);
    if (idx < 0 || !lis[idx]) continue;
    const template = lis[idx];
    const templateId = dv360Id(template);
    const ids = mintRefIds(existingIds(), templateId, job.count);
    const owner = templateId != null ? ownerOf(templateId) : null;
    for (const newId of ids) {
      const copy = clone(template);
      if (templateId != null) copy.lineItemId = newId;
      // Keep the name distinguishable — two rows with one name is a support
      // ticket, and the launcher does not enforce uniqueness.
      if (copy.displayName && !job.overrides.name) copy.displayName = `${copy.displayName} (copy)`;
      lis.push(copy);
      // Adopt the template's insertion order so the new line item is actually
      // launched — an entity no IO references is silently dropped.
      if (owner) {
        if (!Array.isArray(owner.lineItems)) owner.lineItems = [];
        owner.lineItems.push(newId);
      }
      created.push({ rowId: templateId != null ? newId : `li-${lis.length - 1}`, overrides: job.overrides });
    }
  }

  // ── deletes (descending index; prune refs) ──
  const delIdx = [...new Set(deletes.map((op) => dv360IndexOf(lis, op.rowId)).filter((i) => i >= 0))]
    .sort((a, b) => b - a);
  for (const i of delIdx) {
    const victim = lis[i];
    if (!victim) continue;
    const vid = dv360Id(victim);
    lis.splice(i, 1);
    if (vid != null) {
      for (const io of ios) {
        if (!Array.isArray(io.lineItems)) continue;
        io.lineItems = io.lineItems.filter((r) => String(r) !== String(vid));
      }
    }
    deleted.push(vid != null ? String(vid) : `li-${i}`);
  }

  return { created, deleted };
}

// ---------------------------------------------------------------------------
// TTD — AdGroups[] entities, Campaigns[].AdGroups[] string refs.
// Both casings appear in the wild (MMGY `adGroupId`, copy templates
// `AdGroupId`), so read and write whichever the entity already carries.
// ---------------------------------------------------------------------------

const ttdId = (ag) => ag?.adGroupId ?? ag?.AdGroupId ?? null;

function ttdIndexOf(ags, rowId) {
  const byId = ags.findIndex((ag) => String(ttdId(ag)) === String(rowId));
  if (byId >= 0) return byId;
  const m = /^ag-(\d+)$/.exec(String(rowId));
  return m ? Number(m[1]) : -1;
}

function ttdSetId(ag, id) {
  // Mirror the spelling already present so a strict copy template doesn't gain
  // a new key (same rule applyTtdFields follows for names).
  if ('adGroupId' in ag) ag.adGroupId = id;
  if ('AdGroupId' in ag) ag.AdGroupId = id;
  if (!('adGroupId' in ag) && !('AdGroupId' in ag)) ag.AdGroupId = id;
}

export function applyTtdRowOps(edited, ops) {
  const created = [];
  const deleted = [];
  const { creates, clones, deletes } = partition(ops);
  if (!creates.length && !clones.length && !deletes.length) return { created, deleted };

  if (!Array.isArray(edited.AdGroups)) edited.AdGroups = [];
  const ags = edited.AdGroups;
  const campaigns = asArray(edited.Campaigns);

  const refsOf = (c) => (Array.isArray(c.AdGroups) ? c.AdGroups : (Array.isArray(c.adGroups) ? c.adGroups : null));
  const ownerOf = (id) => campaigns.find((c) => asArray(refsOf(c)).some((r) => String(r) === String(id)));
  const existingIds = () => new Set(ags.map(ttdId).filter(Boolean).map(String));

  const cloneJobs = [
    ...clones.map((op) => ({ templateRowId: op.rowId, count: 1, overrides: {} })),
    ...creates.map((op) => ({
      templateRowId: op.templateRowId,
      count: Math.max(1, Number(op.count) || 1),
      overrides: op.overrides || {},
    })),
  ];
  for (const job of cloneJobs) {
    const idx = ttdIndexOf(ags, job.templateRowId);
    if (idx < 0 || !ags[idx]) continue;
    const template = ags[idx];
    const templateId = ttdId(template);
    const ids = mintRefIds(existingIds(), templateId, job.count);
    const owner = templateId != null ? ownerOf(templateId) : null;
    for (const newId of ids) {
      const copy = clone(template);
      if (templateId != null) ttdSetId(copy, newId);
      if (!job.overrides.name) {
        if (copy.adGroupName) copy.adGroupName = `${copy.adGroupName} (copy)`;
        if (copy.AdGroupName) copy.AdGroupName = `${copy.AdGroupName} (copy)`;
      }
      ags.push(copy);
      if (owner) {
        const refs = refsOf(owner);
        if (refs) refs.push(newId);
        else owner.AdGroups = [newId];
      }
      created.push({ rowId: templateId != null ? newId : `ag-${ags.length - 1}`, overrides: job.overrides });
    }
  }

  const delIdx = [...new Set(deletes.map((op) => ttdIndexOf(ags, op.rowId)).filter((i) => i >= 0))]
    .sort((a, b) => b - a);
  for (const i of delIdx) {
    const victim = ags[i];
    if (!victim) continue;
    const vid = ttdId(victim);
    ags.splice(i, 1);
    if (vid != null) {
      for (const c of campaigns) {
        const refs = refsOf(c);
        if (!refs) continue;
        const pruned = refs.filter((r) => String(r) !== String(vid));
        if (Array.isArray(c.AdGroups)) c.AdGroups = pruned;
        else c.adGroups = pruned;
      }
    }
    deleted.push(vid != null ? String(vid) : `ag-${i}`);
  }

  return { created, deleted };
}

// ---------------------------------------------------------------------------
// Google Ads — campaigns[ci].{ad_groups|asset_groups}[ai], purely positional.
// There are no ids pre-launch, so a row id IS the address and a delete
// necessarily renumbers its siblings. Deletes therefore run descending by
// (ci, ai) and callers must re-read rows afterwards.
// ---------------------------------------------------------------------------

const GADS_ROW_RE = /^gads-(\d+)-(\d+)$/;

function gadsUnits(campaign) {
  if (Array.isArray(campaign?.ad_groups) && campaign.ad_groups.length) {
    return { key: 'ad_groups', arr: campaign.ad_groups };
  }
  if (Array.isArray(campaign?.asset_groups) && campaign.asset_groups.length) {
    return { key: 'asset_groups', arr: campaign.asset_groups };
  }
  return null;
}

export function applyGadsRowOps(edited, ops) {
  const created = [];
  const deleted = [];
  const { creates, clones, deletes } = partition(ops);
  if (!creates.length && !clones.length && !deletes.length) return { created, deleted };

  const campaigns = asArray(edited.campaigns);
  const parse = (rowId) => {
    const m = GADS_ROW_RE.exec(String(rowId));
    return m ? { ci: Number(m[1]), ai: Number(m[2]) } : null;
  };

  const cloneJobs = [
    ...clones.map((op) => ({ templateRowId: op.rowId, count: 1, overrides: {} })),
    ...creates.map((op) => ({
      templateRowId: op.templateRowId,
      count: Math.max(1, Number(op.count) || 1),
      overrides: op.overrides || {},
    })),
  ];
  for (const job of cloneJobs) {
    const addr = parse(job.templateRowId);
    if (!addr) continue;
    const campaign = campaigns[addr.ci];
    const units = gadsUnits(campaign);
    // With no ad_groups the row IS the campaign; cloning a campaign is out of
    // scope here (it would need its own budget + name uniqueness handling).
    if (!units || !units.arr[addr.ai]) continue;
    for (let n = 0; n < job.count; n++) {
      const copy = clone(units.arr[addr.ai]);
      if (copy.name && !job.overrides.name) copy.name = `${copy.name} (copy)`;
      units.arr.push(copy);
      created.push({ rowId: `gads-${addr.ci}-${units.arr.length - 1}`, overrides: job.overrides });
    }
  }

  const addrs = deletes.map((op) => parse(op.rowId)).filter(Boolean)
    .sort((a, b) => (b.ci - a.ci) || (b.ai - a.ai));
  for (const { ci, ai } of addrs) {
    const units = gadsUnits(campaigns[ci]);
    if (!units || !units.arr[ai]) continue;
    units.arr.splice(ai, 1);
    deleted.push(`gads-${ci}-${ai}`);
  }

  return { created, deleted };
}

// ---------------------------------------------------------------------------
// Facebook — a flat ad_sets[] under one campaign, positional `fb-${idx}`.
//
// The simplest shape of the four: no parent ref array to prune (a Facebook
// campaign does not list its ad sets — the ad sets ARE the campaign's children
// by containment), and no entity ids pre-launch, so a row id IS the address.
// Deletes therefore renumber their siblings and run descending, exactly as on
// Google Ads and GroundTruth.
//
// The ad-set array key varies: the launch template writes 'Ad Sets' and the
// preprocessor normalises to 'ad_sets'. Ops go through whichever key the plan
// actually carries, so a clone lands in the same array the row builder reads.
// ---------------------------------------------------------------------------

const FB_ROW_RE = /^fb-(\d+)$/;

// Mirrors fbRowBuilders.fbAdSetsKey. Duplicated rather than imported because
// this module is deliberately field-agnostic and imports nothing from the
// per-platform builders; the key list is three lines and one contract.
function fbAdSets(edited) {
  for (const key of ['ad_sets', 'Ad Sets', 'adSets', 'AdSets', 'adsets']) {
    if (Array.isArray(edited?.[key])) return edited[key];
  }
  return null;
}

export function applyFbRowOps(edited, ops) {
  const created = [];
  const deleted = [];
  const { creates, clones, deletes } = partition(ops);
  if (!creates.length && !clones.length && !deletes.length) return { created, deleted };

  const adSets = fbAdSets(edited);
  if (!adSets) return { created, deleted };
  const parse = (rowId) => {
    const m = FB_ROW_RE.exec(String(rowId));
    return m ? Number(m[1]) : -1;
  };

  const cloneJobs = [
    ...clones.map((op) => ({ templateRowId: op.rowId, count: 1, overrides: {} })),
    ...creates.map((op) => ({
      templateRowId: op.templateRowId,
      count: Math.max(1, Number(op.count) || 1),
      overrides: op.overrides || {},
    })),
  ];
  for (const job of cloneJobs) {
    const idx = parse(job.templateRowId);
    if (idx < 0 || !adSets[idx]) continue;
    for (let n = 0; n < job.count; n++) {
      const copy = clone(adSets[idx]);
      // Name the copy off whichever half the row builder renders. An ad set
      // usually has no `name` — it is composed from package + suffix — so
      // suffixing the suffix is what actually shows up in the grid and in Ads
      // Manager. An explicit override wins over both.
      if (!job.overrides.name && !job.overrides.category) {
        if (copy.name) copy.name = `${copy.name} (copy)`;
        else if (copy.ad_set_suffix) copy.ad_set_suffix = `${copy.ad_set_suffix} (copy)`;
        else copy.ad_set_suffix = 'copy';
      }
      adSets.push(copy);
      created.push({ rowId: `fb-${adSets.length - 1}`, overrides: job.overrides });
    }
  }

  const idxs = deletes.map((op) => parse(op.rowId)).filter((i) => i >= 0)
    .sort((a, b) => b - a);
  for (const i of idxs) {
    if (!adSets[i]) continue;
    adSets.splice(i, 1);
    deleted.push(`fb-${i}`);
  }

  return { created, deleted };
}

// ---------------------------------------------------------------------------
// GroundTruth — adgroups[] on the CTS config, positional `gt-ag-${idx}`.
// ---------------------------------------------------------------------------

export function applyGtRowOps(config, ops) {
  const created = [];
  const deleted = [];
  const { creates, clones, deletes } = partition(ops);
  if (!creates.length && !clones.length && !deletes.length) return { created, deleted };

  if (!Array.isArray(config.adgroups)) config.adgroups = [];
  const ags = config.adgroups;
  const parse = (rowId) => {
    const m = /^gt-ag-(\d+)$/.exec(String(rowId));
    return m ? Number(m[1]) : -1;
  };

  const cloneJobs = [
    ...clones.map((op) => ({ templateRowId: op.rowId, count: 1, overrides: {} })),
    ...creates.map((op) => ({
      templateRowId: op.templateRowId,
      count: Math.max(1, Number(op.count) || 1),
      overrides: op.overrides || {},
    })),
  ];
  for (const job of cloneJobs) {
    const idx = parse(job.templateRowId);
    if (idx < 0 || !ags[idx]) continue;
    for (let n = 0; n < job.count; n++) {
      const copy = clone(ags[idx]);
      // GT's config key is ad_group_name — the old adgroup_name spelling was
      // dead code, so un-renamed clones were indistinguishable from their
      // template in the grid (UAT 0819).
      if (copy.ad_group_name && !job.overrides.name) copy.ad_group_name = `${copy.ad_group_name} (copy)`;
      ags.push(copy);
      created.push({ rowId: `gt-ag-${ags.length - 1}`, overrides: job.overrides });
    }
  }

  const delIdx = [...new Set(deletes.map((op) => parse(op.rowId)).filter((i) => i >= 0))]
    .sort((a, b) => b - a);
  for (const i of delIdx) {
    if (!ags[i]) continue;
    ags.splice(i, 1);
    deleted.push(`gt-ag-${i}`);
  }

  return { created, deleted };
}
